"use client";
import { TODOS } from "@/lib/idiomas";
import React, { useState, useEffect, useRef, useMemo } from "react";
import Image from "next/image";
import Cookies from "js-cookie";
import { usePaisDetectado } from "@/lib/geo/pais";
import api from "@/lib/api";
import { resolvePacks } from "@/lib/packs";
import axios from "axios";
import { usePathname } from "next/navigation";
import { useSearchParamsDelVisitante } from "@/lib/useSearchParamsDelVisitante";
import { useTranslation, Trans } from "react-i18next";
import FullScreenLoader from "@/components/common/FullScreenLoader";
import Testimonials from "@/components/homes/_collection/Testimonials";
import BarraNumeros from "@/components/homes/_collection/BarraNumeros";
import { trackViewItemList, trackAddToCart, trackAddToCartPack, sendGA } from "@/lib/analytics";
import { mailDeLaUrl } from "@/lib/mailDeLaUrl";
// Las dos primitivas del cálculo de pack salen del módulo del checkout, que es el
// testeado y el que espeja al PHP. Reimplementarlas acá es lo que hacía que la
// card anunciara un precio y el checkout cobrara otro.
import { quantizeToCurrency, chargeOriginal } from "@/lib/checkout/pricing";
import { isPreorderMeta } from "@/lib/books/preorder";

// Todas las tapas comparten el mismo aspecto apaisado (√2 ≈ 1.414 = ancho/alto).
// Declarar ese ratio en el width/height del <Image> evita que el placeholder
// nazca cuadrado (1:1): dentro de un flex row, una tapa aún sin cargar empujaba
// el alto de la fila y estiraba a sus hermanas ya cargadas (se veía sobre todo
// en iOS/Safari). coverH() deriva el alto entero a partir de un ancho dado.
const COVER_RATIO = 1.414; // ancho / alto
const coverH = (w) => Math.round(w / COVER_RATIO);

const normalizeCC = (value) => {
  const cc = String(value || "")
    .trim()
    .slice(0, 2)
    .toUpperCase();
  return /^[A-Z]{2}$/.test(cc) ? cc : "";
};

const EUROPE_ISO_SET = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
  "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK",
  "SI", "ES", "SE", "IS", "LI", "NO", "CH", "GB", "AL", "RS", "UA", "MD",
  "BY", "GE", "AM", "AZ", "AD", "MC", "SM", "VA", "ME", "BA", "MK", "XK",
]);

const PRODUCT_IDS = {
  ARMONIA: 1,
  MODOS: 11,
  ARMONIA2: 21,
  TENSIONES: 31,
  GLUE: 41,
  ARREGLOS: 51,
  ESCALAS: 61,
};

const PACK_CONFIG = {
  panorama: [PRODUCT_IDS.ARMONIA, PRODUCT_IDS.MODOS, PRODUCT_IDS.TENSIONES],
  futuro: [PRODUCT_IDS.ARMONIA, PRODUCT_IDS.MODOS, PRODUCT_IDS.TENSIONES, PRODUCT_IDS.ARMONIA2, PRODUCT_IDS.ARREGLOS, PRODUCT_IDS.ESCALAS],
};

// Todos los libros vendibles individualmente (orden de catálogo). Se usa para el modo
// "Completá tu colección" cuando la persona ya tiene más de un libro: listamos los que
// le faltan, no packs.
const ALL_BOOK_IDS = [
  PRODUCT_IDS.ARMONIA, PRODUCT_IDS.MODOS, PRODUCT_IDS.TENSIONES,
  PRODUCT_IDS.ARMONIA2, PRODUCT_IDS.GLUE, PRODUCT_IDS.ARREGLOS, PRODUCT_IDS.ESCALAS,
];

// Metadata por libro (tapa, color, slug, key de título/analytics). Espejo del catálogo.
// pages/apps por libro (de los subtítulos del catálogo). `preorderLangs` = idiomas
// donde el libro todavía no se entrega (Escalas salió en español el 15-ago-2026 y
// el resto recién el 1-sep): preguntar con isPreorderMeta, nunca por un booleano.
const BOOK_META = {
  [PRODUCT_IDS.ARMONIA]:   { titleKey: "illustrated_harmony_title",      subtitleKey: "armonia_ilustrada_subtitle",   pages: 153, apps: 2, cover: "tapa_armonia_ilustrada.jpg",                 ext: "jpg", color: "#d0ac22", slug: "ai",       analyticsKey: "armonia" },
  [PRODUCT_IDS.MODOS]:     { titleKey: "illustrated_modes_title",        subtitleKey: "modos_ilustrados_subtitle",    pages: 190, apps: 1, cover: "tapa_modos_ilustrados.jpg",                  ext: "jpg", color: "#e588b5", slug: "mi",       analyticsKey: "modos" },
  [PRODUCT_IDS.TENSIONES]: { titleKey: "illustrated_tensions_title",     subtitleKey: "tensiones_ilustradas_subtitle", pages: 209, apps: 4, cover: "tapa_tensiones_ilustradas.jpg",             ext: "jpg", color: "#81d0e1", slug: "ti",       analyticsKey: "tensiones" },
  [PRODUCT_IDS.ARMONIA2]:  { titleKey: "illustrated_harmony_2_title",    subtitleKey: "armonia_ilustrada_2_subtitle", pages: 218, apps: 2, cover: "tapa_armonia_ilustrada_2.jpg",               ext: "jpg", color: "#95ca44", slug: "ai2",      analyticsKey: "armonia2" },
  [PRODUCT_IDS.GLUE]:      { titleKey: "magic_glue_title",               subtitleKey: "glue_magico_subtitle",         pages: 180, apps: 4, cover: "tapa_glue_magico_para_guitarristas.jpg",     ext: "jpg", color: "#c45646", slug: "glue",     analyticsKey: "glue" },
  [PRODUCT_IDS.ARREGLOS]:  { titleKey: "illustrated_arrangements_title", subtitleKey: "arreglos_ilustrados_subtitle", pages: 142, apps: 3, cover: "tapa_arreglos_ilustrados.jpg",               ext: "jpg", color: "#e29f53", slug: "arreglos", analyticsKey: "arreglos" },
  [PRODUCT_IDS.ESCALAS]:   { titleKey: "illustrated_scales_title",       subtitleKey: "escalas_ilustradas_subtitle",  pages: 154, apps: 1, cover: "tapa_escalas_ilustradas.png", ext: "png", color: "#9288ff", slug: "ei",       analyticsKey: "escalas" },
};

const ANALYTICS_BY_ID = {
  [PRODUCT_IDS.ARMONIA]: "armonia", [PRODUCT_IDS.MODOS]: "modos", [PRODUCT_IDS.ARMONIA2]: "armonia2",
  [PRODUCT_IDS.TENSIONES]: "tensiones", [PRODUCT_IDS.GLUE]: "glue", [PRODUCT_IDS.ARREGLOS]: "arreglos",
  [PRODUCT_IDS.ESCALAS]: "escalas",
};

const isValidEmail = (v) => {
  const s = String(v || "").trim();
  return s ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) : false;
};

export default function HomeLibro2({ children }) {
  const { t, i18n } = useTranslation("books");
  const searchParams = useSearchParamsDelVisitante();
  const pathname = usePathname();

  const SUPPORTED = TODOS;
  const langFromPath = React.useMemo(() => {
    const seg = (pathname?.split("/")?.[1] || "").toLowerCase();
    if (SUPPORTED.includes(seg)) return seg;
    const i18nLang = (i18n.language || "").toLowerCase().split("-")[0];
    return SUPPORTED.includes(i18nLang) ? i18nLang : "es";
  }, [pathname, i18n.language]);

  const localePath = (path) => `/${langFromPath}${path}`;

  // === Country detection ===
  const [countryCode, setCountryCode] = usePaisDetectado();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const hasTrackedViewListRef = useRef(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [showAppModal, setShowAppModal] = useState(null);
  const firstPackRef = useRef(null);
  const packPanoramaRef = useRef(null);
  const [openFaq, setOpenFaq] = useState(null);

  // Gallery refs
  const [currentImageIdx, setCurrentImageIdx] = useState(0);
  const galleryRefs = useRef([]);
  const scrollContainerRef = useRef(null);

  const scrollToFirstPack = () => {
    const ref = packPanoramaRef.current || firstPackRef.current;
    if (!ref) return;
    const element = ref;
    const headerOffset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    try {
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    } catch (e) {
      window.scrollTo(0, offsetPosition);
    }
  };

  const [isWideScreen, setIsWideScreen] = useState(false);
  useEffect(() => {
    const checkWidth = () => setIsWideScreen(window.innerWidth > 1000);
    checkWidth();
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  // CTA sticky (mobile): aparece cuando el COMPRAR del hero sale del viewport y
  // se oculta mientras la sección de productos está a la vista (ahí ya hay botones).
  const heroCtaRef = useRef(null);
  const [heroCtaInView, setHeroCtaInView] = useState(true);
  const [productsInView, setProductsInView] = useState(false);
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const heroEl = heroCtaRef.current;
    const prodEl = firstPackRef.current;
    if (!heroEl || !prodEl) return;
    const heroObs = new IntersectionObserver(([e]) => setHeroCtaInView(e.isIntersecting));
    const prodObs = new IntersectionObserver(([e]) => setProductsInView(e.isIntersecting));
    heroObs.observe(heroEl);
    prodObs.observe(prodEl);
    return () => { heroObs.disconnect(); prodObs.disconnect(); };
  }, []);

  // === Pricing state ===
  const [pricesMatrix, setPricesMatrix] = useState(null);
  const [priceMap, setPriceMap] = useState({});
  const [discounts, setDiscounts] = useState({});
  const [currency, setCurrency] = useState("USD");
  const [displayLocale, setDisplayLocale] = useState("es-AR");
  const [pricingCountry, setPricingCountry] = useState(null);
  const [packDiscounts, setPackDiscounts] = useState([]);

  useEffect(() => {
    const handlePageShow = (event) => { if (event.persisted) setIsRedirecting(false); };
    const handlePopState = () => setIsRedirecting(false);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    if (!hasTrackedViewListRef.current && countryCode) {
      hasTrackedViewListRef.current = true;
      trackViewItemList(countryCode === "AR" ? "ARS" : "USD");
    }
  }, [countryCode]);

  // Gallery scroll tracking
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const firstItem = galleryRefs.current[0];
      if (!firstItem) return;
      const itemWidth = firstItem.offsetWidth + 8;
      const scrollLeft = container.scrollLeft;
      const idx = Math.round(scrollLeft / itemWidth);
      setCurrentImageIdx(Math.min(Math.max(idx, 0), 4));
    };
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const q = normalizeCC(searchParams.get("cc"));
    if (q) {
      setCountryCode((prev) => {
        const current = String(prev || "").toUpperCase();
        if (q !== current) {
          Cookies.set("cc", q, { expires: 30, sameSite: "Lax" });
          return q;
        }
        return prev;
      });
    }
  }, [searchParams]);

  // === Fetch prices & global params ===
  useEffect(() => {
    const ac = new AbortController();
    const fetchData = async () => {
      try {
        const pricesRes = await api.get("/api/prices/matrix", {
          params: { product_ids: "1,11,21,31,41,51,61" },
          signal: ac.signal,
        });
        // Packs primero, y recien ahi commiteamos los dos estados juntos: si los packs no
        // se pueden resolver, no seteamos precios y la UI muestra "—" en vez de un pack sin
        // descuento (que el back si cobraria con descuento). Fail-closed.
        const packs = await resolvePacks(pricesRes, { signal: ac.signal });
        setPricesMatrix(pricesRes?.data ?? null);
        setPackDiscounts(packs);
      } catch (e) {
        if (!axios.isCancel(e)) console.error("[home-libro2] Error fetching data:", e);
      }
    };
    fetchData();
    return () => { ac.abort(); };
  }, []);

  // === Process prices ===
  useEffect(() => {
    if (!countryCode || !pricesMatrix) return;
    try {
      const rawCountries = pricesMatrix?.countries ?? [];
      const products = pricesMatrix?.products ?? [];
      const prices = pricesMatrix?.prices ?? [];
      const countries = rawCountries.map((c) => ({
        ...c,
        code: String(c.code || "").toUpperCase(),
        currencyCode: String(c.currencyCode || "").toUpperCase(),
        originalRoundStep: c.originalRoundStep ?? c.original_round_step ?? null,
        packRoundStep: c.packRoundStep ?? c.pack_round_step ?? null,
        displayLocale: c.displayLocale ?? c.display_locale ?? null,
      }));
      if (!countries.length) {
        setPriceMap({}); setDiscounts({}); setCurrency("USD"); setDisplayLocale("en-US");
        return;
      }
      const byCode = {};
      for (const c of countries) byCode[c.code] = c;
      const cc = String(countryCode || "").toUpperCase();
      let base = byCode[cc] || null;
      if (!base && EUROPE_ISO_SET.has(cc)) base = byCode["EU"] || null;
      if (!base) base = byCode["RW"] || null;
      if (!base) base = [...countries].sort((a, b) => (a.id ?? 0) - (b.id ?? 0))[0];
      setPricingCountry(base);

      const discMap = {};
      for (const p of products) {
        const d = Number(p?.discount);
        discMap[p.id] = Number.isFinite(d) ? d : 0;
      }
      setDiscounts(discMap);

      const byProduct = {};
      for (const row of prices) {
        if (Number(row.countryId) === Number(base.id)) {
          byProduct[row.productId] = row.price == null ? null : Number(row.price);
        }
      }
      setPriceMap(byProduct);

      const computedLocale = base.displayLocale || (cc === "AR" ? "es-AR" : "en-US");
      setDisplayLocale(computedLocale);
      setCurrency(base.currencyCode || "USD");
    } catch (e) {
      console.error("[home-libro2] Error processing prices:", e);
      setPriceMap({}); setDiscounts({}); setCurrency("USD");
    }
  }, [countryCode, pricesMatrix]);

  // === Price formatting ===
  const currencyExponent = (curr) => {
    const C = String(curr || "USD").toUpperCase();
    if (["JPY", "KRW", "VND", "CLP", "ISK", "UGX", "PYG", "RWF", "GNF", "XOF", "XAF"].includes(C)) return 0;
    if (["BHD", "JOD", "KWD", "OMR", "TND", "LYD"].includes(C)) return 3;
    return 2;
  };

  const fmtNumber = (value, curr = currency) =>
    typeof value === "number" && !Number.isNaN(value)
      ? new Intl.NumberFormat(displayLocale, {
          minimumFractionDigits: currencyExponent(curr),
          maximumFractionDigits: currencyExponent(curr),
        }).format(value)
      : "—";

  const fmtPrice = (v) => fmtNumber(Number(v), currency);

  const roundToStep = (value, step) => {
    const v = Number(value);
    const s = Number(step);
    if (!Number.isFinite(v) || !Number.isFinite(s) || s <= 0) return value;
    const decimalsIn = (n) => { const str = String(n); const i = str.indexOf("."); return i === -1 ? 0 : str.length - i - 1; };
    const scale = Math.pow(10, decimalsIn(s));
    const V = Math.round(v * scale);
    const S = Math.round(s * scale);
    const r = Math.round(V / S) * S;
    return r / scale;
  };

  // `maxTotal` es lo que costarían esos mismos libros SIN el descuento de pack.
  // Es el techo: un pack nunca puede salir más caro que comprar suelto.
  const roundPackPrice = (value, step, originalTotal, targetDiscount, maxTotal) => {
    const v = Number(value); const s = Number(step);
    if (!Number.isFinite(v) || !Number.isFinite(s) || s <= 0) return value;
    const decimalsIn = (n) => { const str = String(n); const i = str.indexOf("."); return i === -1 ? 0 : str.length - i - 1; };
    const precision = decimalsIn(s);
    const ceiled  = parseFloat((Math.ceil(v / s) * s).toFixed(precision));
    const floored = parseFloat((Math.floor(v / s) * s).toFixed(precision));
    let rounded = ceiled;
    if (targetDiscount != null && Number.isFinite(originalTotal) && originalTotal > 0) {
      const ceilDiscPct = (1 - ceiled / originalTotal) * 100;
      if (ceilDiscPct < targetDiscount) rounded = floored;
    }
    // Techo: si el ceil se pasa de lo que costaría sin pack, va el floor. Sin esto,
    // al completar la colección en los países donde el step pesa mucho contra el
    // precio del libro (IL, PE, HU, CO), declarar los libros que ya tenés salía MÁS
    // CARO que no declararlos: en IL un libro de lista 69 terminaba en 70.
    if (Number.isFinite(maxTotal) && maxTotal > 0 && rounded > maxTotal) rounded = floored;
    return rounded;
  };

  const computeOriginalFromDiscount = (price, discountPct) => {
    const d = Number(discountPct);
    if (Number.isFinite(price) && price > 0 && Number.isFinite(d) && d > 0 && d < 100) {
      const raw = price / (1 - d / 100);
      const step = Number(pricingCountry?.originalRoundStep);
      return Number.isFinite(step) && step > 0 ? roundToStep(raw, step) : raw;
    }
    return null;
  };

  const matchPackDiscount = (productIds) => {
    const idSet = new Set(productIds);
    let best = null;
    for (const pack of packDiscounts) {
      const pids = pack.product_ids || [];
      if (pids.length > 0 && pids.every((id) => idSet.has(id))) {
        if (!best || pack.discount_pct > best.discount_pct) best = pack;
      }
    }
    return best;
  };

  // El target_discount es la promesa del pack COMPLETO ("30% off llevando los 5"),
  // así que solo corre si TODOS los productos del pack están en el CARRITO. Los
  // poseídos siguen habilitando el descuento del pack, lo único que no habilitan
  // es el gate: con un packRoundStep que pesa 13-21% del precio de un libro suelto,
  // la rama del floor regalaba hasta 33% y daba vuelta el orden de los precios (en
  // IL el libro de lista 75 salía 50 y el de lista 69 salía 60).
  const packCompletoEnCarrito = (packIds, cartIds) =>
    !!packIds?.size && [...packIds].every((id) => cartIds.has(id));

  const calculatePackPrices = (productIds) => {
    let totalOriginal = 0;
    let allPricesAvailable = true;
    for (const id of productIds) {
      const price = priceMap[id];
      const discount = discounts[id] || 0;
      if (price == null) { allPricesAvailable = false; continue; }
      const original = computeOriginalFromDiscount(price, discount);
      totalOriginal += original != null ? original : price;
    }
    const matched = matchPackDiscount(productIds);
    const packPct = matched?.discount_pct || 0;
    const packPids = new Set(matched?.product_ids || []);
    let finalPrice = 0;
    let maxTotal = 0;
    let gateOriginal = 0;
    const exponent = currencyExponent(currency);
    for (const id of productIds) {
      const price = priceMap[id];
      if (price == null) continue;
      // Cuantizado por item y original sin redondear para el gate: los dos criterios
      // del checkout y del backend (ver calculateCompletionPrices).
      finalPrice += packPids.has(id) && packPct > 0
        ? quantizeToCurrency(price * (1 - packPct / 100), exponent)
        : price;
      gateOriginal += chargeOriginal(price, discounts[id] || 0, exponent);
      maxTotal += price;
    }
    // Round pack total to packRoundStep (ceil first, floor only if ceil drops below
    // target, y nunca por encima de lo que costaría sin descuento de pack)
    const packStep = Number(pricingCountry?.packRoundStep);
    if (Number.isFinite(packStep) && packStep > 0) {
      // Acá se cobra TODO el pack, así que el gate del target_discount corre siempre.
      finalPrice = roundPackPrice(finalPrice, packStep, gateOriginal, matched?.target_discount, maxTotal);
    }
    const totalDiscountPct = totalOriginal > 0 ? Math.round((1 - finalPrice / totalOriginal) * 100) : 0;
    return { original: totalOriginal, final: finalPrice, discountPct: totalDiscountPct, allPricesAvailable };
  };

  const packPrices = useMemo(() => ({
    panorama: calculatePackPrices(PACK_CONFIG.panorama),
    futuro: calculatePackPrices(PACK_CONFIG.futuro),
  }), [priceMap, discounts, pricingCountry, packDiscounts]);

  const getBookPrice = (productId) => {
    const price = priceMap[productId];
    const discount = discounts[productId] || 0;
    const original = computeOriginalFromDiscount(price, discount);
    const discountPct = (original != null && original > price) ? Math.round((1 - price / original) * 100) : 0;
    return { price, original, hasDiscount: original != null && original > price, discountPct };
  };

  const armoniaPrice = getBookPrice(PRODUCT_IDS.ARMONIA);

  // ============================================================
  // === "¿Ya compraste alguno de los libros?" → completar colección
  // ============================================================
  const emailParam = mailDeLaUrl(searchParams);
  const hasEmailParam = isValidEmail(emailParam);

  // null = todavía no se consultó. Solo seteamos un array cuando hay ≥1 libro.
  const [ownedBaseIds, setOwnedBaseIds] = useState(null);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [fromParam, setFromParam] = useState(false);
  const [showOwnedModal, setShowOwnedModal] = useState(false);
  const [modalEmail, setModalEmail] = useState("");
  const [ownedLoading, setOwnedLoading] = useState(false);
  const [ownedError, setOwnedError] = useState("");

  const scrollToOptions = () => {
    const el = firstPackRef.current;
    if (!el) return;
    const headerOffset = 80;
    const top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    try { window.scrollTo({ top, behavior: "smooth" }); } catch { window.scrollTo(0, top); }
  };

  const fetchOwned = async (rawEmail, { auto = false } = {}) => {
    const email = String(rawEmail || "").trim().toLowerCase();
    if (!isValidEmail(email)) { setOwnedError(t("l2_owned_error_invalid")); return; }
    setOwnedLoading(true);
    setOwnedError("");
    try {
      const { data } = await api.post("/api/owned-books", { email });
      const owned = Array.isArray(data?.owned)
        ? data.owned.map(Number).filter((id) => ALL_BOOK_IDS.includes(id))
        : [];
      if (!owned.length) {
        // No es comprador / sin libros: en modo auto (link con ?email) no molestamos
        // y dejamos la vista normal; en modo manual avisamos en el modal.
        // Reactivamos el CTA (fromParam=false) para que tenga forma de reintentar
        // con otro mail si el del link no es comprador.
        if (auto) { setOwnedBaseIds(null); setFromParam(false); }
        else setOwnedError(t("l2_owned_error_not_found"));
        return;
      }
      setOwnedBaseIds(owned);
      setOwnerEmail(email);
      setShowOwnedModal(false);
      setTimeout(scrollToOptions, 60); // dejar pintar el nuevo layout
    } catch (e) {
      if (axios.isCancel?.(e)) return;
      console.error("[home-libro2] owned-books error:", e);
      setOwnedError(t("l2_owned_error_generic"));
    } finally {
      setOwnedLoading(false);
    }
  };

  // ?email= (o ?m=) → auto, como si hubiera ingresado el mail. Oculta el CTA.
  const autoFetchedRef = useRef(false);
  useEffect(() => {
    if (autoFetchedRef.current || !hasEmailParam) return;
    autoFetchedRef.current = true;
    setFromParam(true);
    fetchOwned(emailParam, { auto: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasEmailParam, emailParam]);

  const ownedSet = useMemo(() => new Set(ownedBaseIds || []), [ownedBaseIds]);

  // default | complete-armonia | missing-books | all-owned
  const ownershipMode = useMemo(() => {
    const owns = (ownedBaseIds || []).filter((id) => ALL_BOOK_IDS.includes(id));
    if (!owns.length) return "default";
    if (ALL_BOOK_IDS.every((id) => ownedSet.has(id))) return "all-owned";
    if (owns.length === 1 && owns[0] === PRODUCT_IDS.ARMONIA) return "complete-armonia";
    return "missing-books";
  }, [ownedBaseIds, ownedSet]);

  const missingBookIds = useMemo(
    () => ALL_BOOK_IDS.filter((id) => !ownedSet.has(id)),
    [ownedSet]
  );

  // Precio para "completar" un pack: el descuento califica con el pack COMPLETO
  // (igual que el backend, que suma los libros ya poseídos al set de match), pero
  // se cobra solo lo que falta. Espeja calculatePackPrices sobre el subconjunto no poseído.
  const calculateCompletionPrices = (packProductIds, ownedIdsSet) => {
    const unowned = packProductIds.filter((id) => !ownedIdsSet.has(id));
    const matched = matchPackDiscount(packProductIds);
    const packPct = matched?.discount_pct || 0;
    const packPids = new Set(matched?.product_ids || []);

    let finalPrice = 0;
    let originalTotal = 0;
    // gateOriginal = el mismo original pero SIN redondear al originalRoundStep.
    // El redondeado sirve para mostrar tachado; el gate del target_discount tiene
    // que usar el crudo, que es el que usa el backend (con el redondeado, en JP
    // el gate elegía distinto y eran 100 yenes de diferencia).
    let gateOriginal = 0;
    // maxTotal = lo que costarían los libros QUE SE COMPRAN (unowned) sin el
    // descuento de pack. Es el techo del ceil (ver roundPackPrice).
    let maxTotal = 0;
    let allPricesAvailable = true;
    const exponent = currencyExponent(currency);
    for (const id of unowned) {
      const price = priceMap[id];
      if (price == null) { allPricesAvailable = false; continue; }
      const disc = discounts[id] || 0;
      const original = computeOriginalFromDiscount(price, disc);
      originalTotal += original != null ? original : price;
      gateOriginal += chargeOriginal(price, disc, exponent);
      // Cuantizado a la moneda ANTES de sumar, igual que el checkout y el PHP:
      // sumando en float, en AU las milésimas cruzaban el borde del step y la
      // card anunciaba 80 AUD mientras el checkout cobraba 79.
      finalPrice += packPids.has(id) && packPct > 0
        ? quantizeToCurrency(price * (1 - packPct / 100), exponent)
        : price;
      maxTotal += price;
    }
    const packStep = Number(pricingCountry?.packRoundStep);
    if (Number.isFinite(packStep) && packStep > 0) {
      // El gate del target_discount solo corre si el pack entero está en el carrito
      // (unowned), no cuando lo completan los libros ya poseídos.
      const gate = packCompletoEnCarrito(packPids, new Set(unowned)) ? matched?.target_discount : null;
      finalPrice = roundPackPrice(finalPrice, packStep, gateOriginal, gate, maxTotal);
    }
    const discountPct = originalTotal > 0 ? Math.round((1 - finalPrice / originalTotal) * 100) : 0;
    return { unowned, original: originalTotal, final: finalPrice, discountPct, allPricesAvailable };
  };

  const completionPrices = useMemo(() => ({
    panorama: calculateCompletionPrices(PACK_CONFIG.panorama, ownedSet),
    futuro: calculateCompletionPrices(PACK_CONFIG.futuro, ownedSet),
  }), [priceMap, discounts, pricingCountry, packDiscounts, ownedSet]);

  const onBuyCompletionClick = (slug, packProductIds) => async () => {
    if (isRedirecting) return;
    const unowned = packProductIds.filter((id) => !ownedSet.has(id));
    const ownedInPack = packProductIds.filter((id) => ownedSet.has(id));
    const qs = `products=${unowned.join(",")}`
      + (ownedInPack.length ? `&owned=${ownedInPack.join(",")}` : "")
      + (ownerEmail ? `&email=${encodeURIComponent(ownerEmail)}` : "");
    const fallback = `/checkout-pay?${qs}`;
    const dest = (() => { try { return localePath(fallback); } catch { return fallback; } })();
    try {
      setIsRedirecting(true);
      const safety = setTimeout(() => { window.location.href = dest; }, 10000);
      const packName = slug === "futuro" ? "Pack Futuro" : "Pack Panorama";
      const price = completionPrices[slug]?.final || 0;
      try { await trackAddToCartPack(slug, packName, price, currency); } catch (err) { console.error("Error tracking completion add_to_cart", err); }
      clearTimeout(safety);
    } catch (err) {
      console.error("Error in completion buy handler", err);
    } finally {
      window.location.href = dest;
    }
  };

  const submitOwnedModal = (e) => {
    e?.preventDefault?.();
    fetchOwned(modalEmail);
  };

  // === Buy handlers ===
  const PACK_SLUG_BY_IDS = {
    [PACK_CONFIG.panorama.join(",")]: "panorama",
    [PACK_CONFIG.futuro.join(",")]: "futuro",
  };

  const onBuyPackClick = (productIds) => async () => {
    if (isRedirecting) return;
    const fallback = `/checkout-pay?products=${productIds.join(",")}`;
    const dest = (() => { try { return localePath(fallback); } catch { return fallback; } })();
    try {
      setIsRedirecting(true);
      const safetyTimeout = setTimeout(() => { window.location.href = dest; }, 10000);
      const slug = PACK_SLUG_BY_IDS[productIds.join(",")];
      if (slug) {
        const packName = slug === "futuro" ? "Pack Futuro" : "Pack Panorama";
        const price = packPrices[slug]?.final || 0;
        try { await trackAddToCartPack(slug, packName, price, currency); } catch (err) { console.error("Error tracking pack add_to_cart", err); }
      }
      clearTimeout(safetyTimeout);
    } catch (outerErr) {
      console.error("Error in pack buy handler", outerErr);
    } finally {
      window.location.href = dest;
    }
  };

  const onBuyBookClick = (productId) => async () => {
    if (isRedirecting) return;
    const fallback = `/checkout-pay?products=${productId}`
      + (ownerEmail ? `&email=${encodeURIComponent(ownerEmail)}` : "");
    const dest = (() => { try { return localePath(fallback); } catch { return fallback; } })();
    try {
      setIsRedirecting(true);
      const safetyTimeout = setTimeout(() => { window.location.href = dest; }, 10000);
      const trackingNames = ANALYTICS_BY_ID;
      try {
        await trackAddToCart(trackingNames[productId] || String(productId), currency, priceMap[productId]);
      } catch (err) { console.error("Error tracking book add_to_cart", err); }
      clearTimeout(safetyTimeout);
    } catch (outerErr) {
      console.error("Error in book buy handler", outerErr);
    } finally {
      window.location.href = dest;
    }
  };

  // === Derivados para la sección "Elegí tu opción" según ownership ===
  const completing = ownershipMode === "complete-armonia";
  const armoniaOwned = completing; // en este modo solo tiene Armonía
  const panoramaPP = completing ? completionPrices.panorama : packPrices.panorama;
  const futuroPP   = completing ? completionPrices.futuro   : packPrices.futuro;
  const onBuyPanorama = completing
    ? onBuyCompletionClick("panorama", PACK_CONFIG.panorama)
    : onBuyPackClick(PACK_CONFIG.panorama);
  const onBuyFuturo = completing
    ? onBuyCompletionClick("futuro", PACK_CONFIG.futuro)
    : onBuyPackClick(PACK_CONFIG.futuro);

  const optionsTitle =
    ownershipMode === "all-owned" ? t("l2_all_owned_title")
    : ownershipMode === "default" ? t("l2_products_title")
    : t("l2_complete_collection_title");
  const optionsSubtitle =
    ownershipMode === "complete-armonia" ? t("l2_complete_collection_subtitle")
    : ownershipMode === "missing-books" ? t("l2_missing_books_subtitle")
    : "";

  // "Usar otro mail": solo abre el modal con el mail actual precargado. NO descarta
  // la vista actual; si el usuario cancela, queda todo como estaba. Recién se
  // reemplaza si confirma un mail nuevo válido (fetchOwned éxito).
  const resetOwned = () => {
    setOwnedError("");
    setModalEmail(ownerEmail || "");
    setShowOwnedModal(true);
  };

  // FAQ data from translations
  const faqItems = t("l2_faq", { returnObjects: true });
  const faqArray = Array.isArray(faqItems) ? faqItems : [];

  return (
    <>
      <FullScreenLoader open={isRedirecting} />

      {/* ========== SECCIÓN 1 — HERO ========== */}
      <section className={`page-section scrollSpysection pt-80 pb-30`} id="about" style={isWideScreen ? { paddingTop: 140 } : {}}>
        <div className="container position-relative">
          <div className="row">
            {/* El aire de arriba en escritorio va por CSS (.libros-hero-col en custom.css)
                y no por isWideScreen: ese estado arranca en false y recien se prende
                despues de hidratar, asi que el titular saltaba 60px con la pagina ya
                dibujada (CLS 0,16 en escritorio segun Search Console). */}
            <div className="col-lg-6 mb-md-60 mb-sm-0 libros-hero-col">
              {/* h1 y no h3: es el titular de la landing y la pagina no tenia ningun h1.
                  Se ve igual: tamaño, peso, interlineado y margen los fijan
                  .section-title, .fw-semibold y .mb-30, no la etiqueta. */}
              <h1 className="section-title mb-30 fw-semibold">
                {t("hero_title")}
              </h1>
              <div className="row">
                <div className="col-lg-10">
                  <p className="text-gray mb-30 mb-sm-20">
                    {t("hero_sub")}
                  </p>
                  <div className="d-flex flex-column align-items-center align-items-lg-center mb-30" style={{ gap: 12 }}>
                    <div
                      ref={heroCtaRef}
                      style={{
                        background: "linear-gradient(120deg, #c45646 0%, #c45646 30%, #475b7e 35%, #475b7e 65%, #e29f53 70%, #e29f53 100%)",
                        padding: "2px",
                        borderRadius: "999px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={scrollToFirstPack}
                        className="btn btn-mod btn-circle btn-large"
                        style={{ background: "#fff", border: "none", borderRadius: "999px", color: "#111" }}
                        data-btn-animate="y"
                      >
                        {t("buy_button")}
                      </button>
                    </div>
                    {/* 2.2 (social proof bajo el CTA) se probó y se sacó: ensuciaba el
                        fold más de lo que sumaba. La línea vive solo en la barra sticky. */}
                    <div className="text-center" style={{ fontSize: 15, lineHeight: "28px" }}>
                      <div className="text-gray">
                        <Trans
                          i18nKey="hero_books_include"
                          ns="books"
                          components={{
                            books: <span style={{ color: "#c45646", fontWeight: 600 }} />,
                            apps: <span style={{ color: "#475b7e", fontWeight: 600 }} />,
                            videos: <span style={{ color: "#e29f53", fontWeight: 600 }} />,
                          }}
                        />
                      </div>
                      <div className="text-gray">{t("hero_complete_proposal")}</div>
                    </div>

                    {/* CTA disimulado: "¿Ya compraste alguno de los libros?" — oculto
                        cuando se entra con ?email= (ya viene resuelto) o cuando ya se
                        cargaron los libros (ahí se usa "usar otro mail" en las opciones). */}
                    {!fromParam && !ownedBaseIds && (
                      <button
                        type="button"
                        onClick={() => { setOwnedError(""); setModalEmail(ownerEmail || ""); setShowOwnedModal(true); }}
                        style={{
                          background: "none", border: "none", padding: "2px 4px",
                          color: "#777", fontSize: 13, textDecoration: "underline",
                          textUnderlineOffset: 3, cursor: "pointer", marginTop: 2,
                        }}
                      >
                        {t("l2_owned_cta")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Gallery */}
            <div className="col-lg-6">
              <div
                ref={scrollContainerRef}
                className="w-100"
                style={{
                  overflowX: "auto", overflowY: "hidden",
                  scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "none", msOverflowStyle: "none",
                }}
              >
                <div className="d-flex" style={{ gap: 8, paddingRight: "8%" }}>
                  {[0, 1, 2, 3, 4].map((num, idx) => {
                    const imgSrc = num === 1 || num === 2
                      ? `/assets/images/libro2/00${num}.png`
                      : `/assets/images/libro2/${langFromPath}/00${num}.png`;
                    return (
                      <div
                        key={num}
                        ref={(el) => (galleryRefs.current[idx] = el)}
                        style={{ flexShrink: 0, width: "92%", scrollSnapAlign: "start", cursor: "pointer" }}
                        onClick={() => {
                          if (idx === currentImageIdx) setLightboxImage(imgSrc);
                          else galleryRefs.current[idx]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
                        }}
                      >
                        <Image
                          // El muro (000) tiene texto con gradiente pastel: el AVIF q=60
                          // del optimizador lo lava (queda apagado). q=80 lo preserva
                          // (~41KB vs 22KB). Las fotos 001-004 toleran bien q=60.
                          width={800} height={800} quality={num === 0 ? 80 : 60}
                          // Sin `sizes`, Next arma srcset 1x/2x y todo DPR>=2 baja la
                          // variante 2x (oversize para el slot de ~360px). El slot es
                          // 92vw en mobile y ~46vw en desktop (col-lg-6 * 92%): declararlo
                          // hace que DPR2 pida w~750 en vez de 1920 (-16KB, sin tocar q).
                          sizes="(min-width: 992px) 46vw, 92vw"
                          className="img-fluid"
                          style={{ aspectRatio: "1/1", objectFit: "cover" }}
                          src={imgSrc}
                          alt={`Libro preview ${idx + 1}`}
                          priority={num === 0}
                          fetchPriority={num === 0 ? "high" : undefined}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Los puntos de la galeria van siempre en el HTML y los muestra el CSS solo en
                  escritorio (.libros-hero-dots en custom.css). Con isWideScreen se montaban
                  despues de hidratar y empujaban 22px todo lo de abajo. */}
              <div className="libros-hero-dots" style={{ justifyContent: "center", gap: 8, marginTop: 12 }}>
                {[0, 1, 2, 3, 4].map((num, idx) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => {
                      const container = scrollContainerRef.current;
                      const targetEl = galleryRefs.current[idx];
                      if (container && targetEl) {
                        const itemWidth = targetEl.offsetWidth + 8;
                        container.scrollTo({ left: idx * itemWidth, behavior: "smooth" });
                      }
                    }}
                    style={{
                      width: 10, height: 10, borderRadius: "50%", border: "none",
                      background: idx === currentImageIdx ? "#333" : "#ccc",
                      cursor: "pointer", padding: 0, transition: "background 0.2s",
                    }}
                    aria-label={`Ver imagen ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* "Soy una persona visual" */}
      <section className={`page-section scrollSpysection pb-0 pt-1 pt-md-4`}>
        <div className="container position-relative">
          <div className="row justify-content-lg-center">
            <div className="col-lg-8 d-flex align-items-center mb-md-20">
              <div>
                <div className="wow linesAnimIn" data-splitting="lines">
                  <h2 className="section-title mb-30 mb-sm-20" style={isWideScreen ? { textAlign: "center" } : {}}>
                    {t("section_1_title")}
                    <span className="text-gray">.</span>
                  </h2>
                  <div className="text-gray mb-30 mb-sm-20" style={isWideScreen ? { textAlign: "center" } : {}}>
                    <p className="mb-0" dangerouslySetInnerHTML={{ __html: t("section_1_text") }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECCIÓN 2 — PRUEBA SOCIAL ========== */}
      <BarraNumeros
        datos={[
          { numero: "+100", etiqueta: t("l2_social_countries") },
          { numero: "+300K", etiqueta: t("l2_social_musicians"), instagram: true },
        ]}
        idiomas={t("l2_social_languages")}
      />

      {/* ========== SECCIÓN 3 — LA PROPUESTA (tapas + items) ========== */}
      <section
        style={{
          background: "#fff",
          padding: "50px 20px 40px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Círculos de fondo con gradientes */}
        {!isWideScreen && (
          <>
            <div style={{ position: "absolute", top: 80, left: -150, width: 300, height: 300, background: "radial-gradient(ellipse at 0% 50%, rgba(208,172,34,1) 20%, rgba(208,172,34,0) 70%)", pointerEvents: "none" }} />
            <div style={{ position: "absolute", top: 60, right: -150, width: 300, height: 300, background: "radial-gradient(ellipse at 100% 50%, rgba(149,202,68,1) 20%, rgba(149,202,68,0) 70%)", pointerEvents: "none" }} />
            <div style={{ position: "absolute", top: 250, left: -150, width: 300, height: 300, background: "radial-gradient(ellipse at 0% 50%, rgba(229,136,181,1) 20%, rgba(229,136,181,0) 70%)", pointerEvents: "none" }} />
            <div style={{ position: "absolute", top: 250, right: -150, width: 300, height: 300, background: "radial-gradient(ellipse at 100% 50%, rgba(129,208,225,1) 20%, rgba(129,208,225,0) 70%)", pointerEvents: "none" }} />
          </>
        )}

        <h2 style={{ color: "#111", fontSize: 46, fontWeight: 700, letterSpacing: 1, marginBottom: 50, position: "relative", zIndex: 1 }}>
          {t("l2_proposal_title")}
        </h2>

        <div style={{
          display: "flex",
          flexDirection: isWideScreen ? "row" : "column",
          alignItems: "center",
          gap: isWideScreen ? 60 : 0,
          maxWidth: isWideScreen ? 900 : "100%",
          width: "100%",
          zIndex: 1,
        }}>
          {/* Items descriptivos */}
          <div style={{
            fontSize: 15,
            maxWidth: 500,
            lineHeight: 1.7,
            textAlign: "left",
            position: "relative",
            zIndex: 1,
            order: isWideScreen ? 1 : 2,
            flex: isWideScreen ? 1 : undefined,
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}>
            {[
              { chord: "C", color: "#ec6696", titleKey: "l2_item_instruments_title", textKey: "l2_item_instruments_text" },
              { chord: "C7", color: "#fcc764", titleKey: "l2_item_visual_title", textKey: "l2_item_visual_text" },
              { chord: "F", color: "#ec6696", titleKey: "l2_item_access_title", textKey: "l2_item_access_text" },
              { chord: "Fm", color: "#6eb9d4", titleKey: "l2_item_diagrams_title", textKey: "l2_item_diagrams_text" },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <svg width="36" height="36" viewBox="0 0 36 36" style={{ flexShrink: 0, marginTop: 0 }}>
                  <circle cx="18" cy="18" r="16" fill="none" stroke={item.color} strokeWidth="2" />
                  <text x="18" y="18" textAnchor="middle" dominantBaseline="central" fill={item.color} fontSize={item.chord.length > 2 ? 11 : 14} fontWeight="900" fontFamily="sans-serif">
                    {item.chord}
                  </text>
                </svg>
                <div>
                  <span style={{ fontWeight: 700, color: "#111" }}>{t(item.titleKey)}</span>
                  <span style={{ color: "#666", marginLeft: 4 }}>{t(item.textKey)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* 4 tapas de libros */}
          <div style={{
            position: "relative",
            width: isWideScreen ? 400 : "min(90vw, 400px)",
            height: isWideScreen ? 320 : "min(72vw, 320px)",
            margin: isWideScreen ? 0 : "0 auto 50px",
            zIndex: 1,
            order: isWideScreen ? 2 : 1,
            flexShrink: 0,
          }}>
            {[
              { src: "tapa_armonia_ilustrada.jpg", alt: "Armonía Ilustrada", top: "0%", left: "2%", rotate: -8, width: "48%", zIndex: 1 },
              { src: "tapa_armonia_ilustrada_2.jpg", alt: "Armonía Ilustrada 2", top: "0%", left: "51%", rotate: 6, width: "48%", zIndex: 2 },
              { src: "tapa_modos_ilustrados.jpg", alt: "Modos Ilustrados", top: "53%", left: "2%", rotate: -4, width: "48%", zIndex: 3 },
              { src: "tapa_tensiones_ilustradas.jpg", alt: "Tensiones Ilustradas", top: "50%", left: "50%", rotate: 5, width: "48%", zIndex: 4 },
            ].map((book, i) => (
              <div
                key={i}
                style={{
                  position: "absolute",
                  top: book.top,
                  left: book.left,
                  width: book.width,
                  transform: `rotate(${book.rotate}deg)`,
                  zIndex: book.zIndex,
                  overflow: "hidden",
                  boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
                  transition: "transform 0.3s ease",
                  cursor: "pointer",
                }}
                onClick={() => setLightboxImage(`/book/${langFromPath}/${book.src}`)}
              >
                <Image
                  src={`/book/${langFromPath}/${book.src}`}
                  alt={book.alt}
                  width={200}
                  height={coverH(200)}
                  quality={60}
                  style={{ width: "100%", height: "auto", objectFit: "cover", display: "block" }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== SECCIÓN 3b — APPS ========== */}
      <section
        style={{
          position: "relative",
          height: 200,
          overflow: "hidden",
          background: "linear-gradient(to bottom, #fff 50%, #191917 50%)",
        }}
      >
        <div style={{ position: "absolute", inset: 0, background: "#c45646", clipPath: "polygon(0 29%, 100% 0%, 100% 56%, 0 84%)", zIndex: 1 }} />
        <div style={{ position: "absolute", inset: 0, background: "#475b7e", clipPath: "polygon(0 22%, 100% 63%, 100% 91%, 0 57%)", zIndex: 2 }} />
        <div style={{ position: "absolute", inset: 0, background: "#e29f53", clipPath: "polygon(0 81%, 100% 66%, 100% 81%, 0 98%)", zIndex: 3 }} />
        <div style={{ position: "absolute", inset: 0, background: "#191917", clipPath: "polygon(0 78%, 100% 46%, 100% 100%, 0 100%)", zIndex: 0 }} />
        <div style={{
          position: "absolute", top: "46%", left: "50%", transform: "translate(-50%, -50%)",
          zIndex: 10, color: "#fff", fontSize: 60, fontWeight: 700, letterSpacing: 4,
          textShadow: "0 2px 10px rgba(0,0,0,0.3)", whiteSpace: "nowrap",
        }}>
          {t("apps_section_title")}
        </div>
      </section>

      <section style={{ background: "#191917", padding: "40px 20px 10px", textAlign: "center", marginTop: -1 }}>
        <p style={{ color: "#999", fontSize: 16, maxWidth: 600, margin: "0 auto 50px", lineHeight: 1.5 }}>
          {t("l2_apps_description")}
        </p>
        <div
          style={isWideScreen ? {
            display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 20, maxWidth: 560, margin: "0 auto", padding: "0 10px",
          } : {
            display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, maxWidth: 380, margin: "0 auto", padding: "0 10px",
          }}
        >
          {[
            { img: "03.png", key: "poliacordes", includedWithKey: "illustrated_tensions_title", includedColor: "#81d0e1" },
            { img: "04.png", key: "freq_visualizer", includedWithKey: "illustrated_tensions_title", includedColor: "#81d0e1" },
            { img: "08.png", key: "chord_finder", includedWith: [{ key: "illustrated_harmony_title", color: "#d0ac22" }, { key: "magic_glue_title", color: "#c45646" }, { key: "illustrated_arrangements_title", color: "#e29f53" }] },
            { img: "06.png", key: "interval_visualizer", includedWith: [{ key: "illustrated_tensions_title", color: "#81d0e1" }, { key: "illustrated_arrangements_title", color: "#e29f53" }] },
            { img: "09.png", key: "harmonizer", includedWithKey: "illustrated_modes_title", includedColor: "#e588b5" },
            { img: "12.png", key: "acordes_vecinos", includedWithKey: "illustrated_harmony_title", includedColor: "#d0ac22" },
            { img: "acordes-piano-roll.png", key: "acordes_piano_roll", includedWith: [{ key: "magic_glue_title", color: "#c45646" }, { key: "illustrated_arrangements_title", color: "#e29f53" }] },
          ].map((app, i, arr) => (
            <div key={i} style={{ textAlign: "center", width: isWideScreen ? 115 : undefined, gridColumn: !isWideScreen && i === arr.length - 1 && arr.length % 3 === 1 ? "2" : undefined }}>
              <Image
                src={`/assets/images/apps/${app.img}`}
                alt={t(`app_${app.key}`)}
                width={120}
                height={120}
                quality={60}
                onClick={() => setShowAppModal(app)}
                style={{ width: "100%", maxWidth: 90, height: "auto", borderRadius: 16, marginBottom: 8, cursor: "pointer" }}
              />
              <p style={{ color: "#999", fontSize: 12, fontWeight: 700, lineHeight: 1.4, margin: 0 }}>
                {t(`app_${app.key}`)}
              </p>
            </div>
          ))}
        </div>
        <p style={{ color: "#999", fontSize: 16, marginTop: 50, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: t("apps_constantly_updating") }} />
      </section>

      {/* ========== SECCIÓN 3c — VIDEOS ========== */}
      <section
        style={{
          position: "relative",
          height: 200,
          overflow: "hidden",
          marginTop: -1,
          background: "#191917",
        }}
      >
        <div style={{ position: "absolute", inset: 0, background: "#c45646", clipPath: "polygon(0px 29%, 100% 0%, 100% 44%, 0px 67%)", zIndex: 1 }} />
        <div style={{ position: "absolute", inset: 0, background: "#475b7e", clipPath: "polygon(0px 72%, 100% 35%, 100% 61%, 0px 98%)", zIndex: 2 }} />
        <div style={{ position: "absolute", inset: 0, background: "#e29f53", clipPath: "polygon(0px 21%, 100% 20%, 100% 33%, 0px 33%)", zIndex: 3 }} />
        <div style={{ position: "absolute", inset: 0, background: "#191917", clipPath: "polygon(0 78%, 100% 46%, 100% 100%, 0 100%)", zIndex: 0 }} />
        <div style={{
          position: "absolute", top: "46%", left: "50%", transform: "translate(-50%, -50%)",
          zIndex: 10, color: "#fff", fontSize: 60, fontWeight: 700, letterSpacing: 4,
          textShadow: "0 2px 10px rgba(0,0,0,0.3)",
        }}>
          {t("videos_section_title")}
        </div>
      </section>

      <section style={{ background: "#191917", padding: "0px 10px 50px", marginTop: -38 }}>
        <div style={{ position: "relative", maxWidth: 600, height: 320, margin: "0 auto 50px" }}>
          <div
            style={{ position: "absolute", right: "5%", bottom: 0, zIndex: 1, width: "80vw", maxWidth: 350, cursor: "pointer" }}
            onClick={() => setLightboxImage("/assets/images/libro2/phone_02.png")}
          >
            <Image
              src="/assets/images/libro2/phone_02.png"
              alt={t("common:horizontal_phone_alt")}
              width={350}
              height={220}
              style={{ width: "100%", height: "auto", objectFit: "contain", filter: "drop-shadow(0 0 1px #475b7e) drop-shadow(0 0 3px #475b7e)" }}
            />
          </div>
          <div
            style={{ position: "absolute", left: "10%", top: 0, zIndex: 2, width: "40vw", maxWidth: 200, cursor: "pointer" }}
            onClick={() => setLightboxImage(`/assets/images/libro2/${langFromPath}/phone_01.png`)}
          >
            <Image
              src={`/assets/images/libro2/${langFromPath}/phone_01.png`}
              alt={t("common:vertical_phone_alt")}
              width={200}
              height={400}
              style={{ width: "100%", height: "auto", objectFit: "contain", filter: "drop-shadow(0 0 1px #475b7e) drop-shadow(0 0 3px #475b7e)" }}
            />
          </div>
        </div>
        <p style={{ color: "#999", fontSize: 16, maxWidth: 600, margin: "0 auto", lineHeight: 1.5, textAlign: "center", padding: "0px 20px", ...(isWideScreen ? { marginTop: 130 } : {}) }}>
          {t("videos_section_description")}
        </p>
      </section>

      {/* ========== SECCIÓN 4 — PRODUCTOS ========== */}
      <section ref={firstPackRef} style={{ background: "#f8f8f8", padding: "60px 20px 30px", scrollMarginTop: 80 }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: "#111", textAlign: "center", marginBottom: optionsSubtitle ? 12 : 50 }}>
            {optionsTitle}
          </h2>
          {optionsSubtitle && (
            <p style={{ textAlign: "center", color: "#666", fontSize: 16, maxWidth: 560, margin: "0 auto 40px", lineHeight: 1.5 }}>
              {optionsSubtitle}
            </p>
          )}

          {ownershipMode === "all-owned" ? (
            <div style={{ textAlign: "center", maxWidth: 480, margin: "0 auto", padding: "20px 0 10px" }}>
              <div style={{ fontSize: 44, marginBottom: 8 }}>🎉</div>
              <p style={{ color: "#666", fontSize: 16, marginBottom: 24, lineHeight: 1.5 }}>{t("l2_all_owned_text")}</p>
              <div style={{ background: "linear-gradient(120deg, #c45646 0%, #475b7e 50%, #e29f53 100%)", padding: 2, borderRadius: 999, display: "inline-block" }}>
                <a href={localePath("/portal")} className="btn btn-mod btn-circle btn-large" style={{ background: "#fff", border: "none", borderRadius: 999, color: "#111", textDecoration: "none" }} data-btn-animate="y">
                  {t("l2_all_owned_cta")}
                </a>
              </div>
            </div>
          ) : ownershipMode === "missing-books" ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, justifyContent: "center", alignItems: "stretch" }}>
              {missingBookIds.map((id) => {
                const meta = BOOK_META[id];
                if (!meta) return null;
                const pricing = getBookPrice(id);
                const cover = `/book/${langFromPath}/${meta.cover}`;
                return (
                  <div key={id} style={{ background: "#fff", borderRadius: 16, padding: "30px 24px", flex: "1 1 280px", maxWidth: 340, width: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column" }}>
                    <h3 style={{ fontSize: 23, fontWeight: 700, color: "#111", textAlign: "center", marginBottom: 16 }}>{t(meta.titleKey)}</h3>
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: 16, cursor: "pointer" }} onClick={() => setLightboxImage(cover)}>
                      <Image src={cover} alt={t(meta.titleKey)} width={200} height={coverH(200)} quality={60} style={{ width: 150, height: "auto", boxShadow: "0 4px 15px rgba(0,0,0,0.15)", border: `3px solid ${meta.color}` }} />
                    </div>
                    <ul style={{ color: "#555", fontSize: 15, textAlign: "center", marginBottom: 16, paddingLeft: 0, lineHeight: 2, listStyle: "none", margin: "0 auto 16px" }}>
                      {meta.pages != null ? (
                        <>
                          <li>✓ {t("pages", { count: meta.pages })}</li>
                          <li>✓ {t("l2_book_apps", { count: meta.apps })}</li>
                          <li>✓ {t("l2_book_videos")}</li>
                        </>
                      ) : (
                        // Sin páginas declaradas: apps y videos sin números.
                        <>
                          <li>✓ {t("l2_book_apps_nocount")}</li>
                          <li>✓ {t("l2_book_videos")}</li>
                        </>
                      )}
                    </ul>
                    <div style={{ marginTop: "auto", textAlign: "center" }}>
                      {/* Preventa: igual que en libros-catalogo, el subtítulo (estado/fecha)
                          va en negrita y color de acento para aclarar que es preventa. */}
                      {isPreorderMeta(meta, langFromPath) && (
                        <div style={{ color: meta.color, fontWeight: 700, fontSize: 13, marginBottom: 10 }}>{t(meta.subtitleKey)}</div>
                      )}
                      {pricing.price != null ? (
                        <>
                          <div style={{ marginBottom: 12 }}>
                            {pricing.discountPct > 0 && (
                              <div><span style={{ background: "#111", color: "#fff", fontSize: 14, padding: "3px 10px", borderRadius: 50, fontWeight: 700 }}>{pricing.discountPct}% OFF</span></div>
                            )}
                            {pricing.hasDiscount && (
                              <span style={{ fontSize: 18, color: "#999", textDecoration: "line-through", whiteSpace: "nowrap", marginTop: 16, display: "inline-block" }}>{currency} {fmtPrice(pricing.original)}</span>
                            )}
                            <span style={{ fontSize: 30, fontWeight: 700, color: "#111", whiteSpace: "nowrap", display: "block", lineHeight: "35px" }}>{currency} {fmtPrice(pricing.price)}</span>
                          </div>
                          <div style={{ background: meta.color, padding: "3px", borderRadius: "999px" }}>
                            <button type="button" onClick={onBuyBookClick(id)} className="btn btn-mod btn-circle btn-large" style={{ background: "#fff", border: "none", borderRadius: "999px", color: "#111", width: "100%" }} data-btn-animate="y" disabled={isRedirecting}>
                              {t("buy_button")}
                            </button>
                          </div>
                          <div style={{ fontSize: 12, color: "#999", marginTop: 10 }}>{t("l2_digital_instant")}</div>
                        </>
                      ) : !isPreorderMeta(meta, langFromPath) ? (
                        // Sin precio y sin ser preventa (no debería pasar con el catálogo actual).
                        <div style={{ fontSize: 13, color: meta.color, fontWeight: 700, marginBottom: 12 }}>{t(meta.subtitleKey)}</div>
                      ) : null}
                      <a href={localePath(`/libro/${meta.slug}`)} style={{ display: "inline-block", marginTop: 12, fontSize: 13, color: "#bbb", textDecoration: "underline", textUnderlineOffset: 3 }}>{t("l2_see_more")}</a>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
          <div style={{
            display: "flex",
            flexDirection: isWideScreen ? "row" : "column",
            gap: 24,
            justifyContent: "center",
            alignItems: isWideScreen ? "stretch" : "center",
          }}>
            {/* Armonía Ilustrada — entrada (o "Ya lo tenés" al completar).
                En mobile va ÚLTIMA (orden: Panorama → Futuro → Armonía, 4.1). */}
            <div style={{
              background: "#fff",
              borderRadius: 16,
              padding: "30px 24px",
              flex: 1,
              maxWidth: 340,
              width: "100%",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              order: isWideScreen ? undefined : 1,
            }}>
              {armoniaOwned && (
                <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: "#2e7d32", color: "#fff", fontSize: 12, fontWeight: 700, padding: "4px 16px", borderRadius: 50, whiteSpace: "nowrap" }}>
                  ✓ {t("l2_owned_badge")}
                </div>
              )}
              <h3 style={{ fontSize: 28, fontWeight: 700, color: armoniaOwned ? "#999" : "#111", textAlign: "center", marginBottom: 16 }}>
                {t("illustrated_harmony_title")}
              </h3>
              <div
                style={{ display: "flex", justifyContent: "center", marginBottom: 12, cursor: "pointer" }}
                onClick={() => setLightboxImage(`/book/${langFromPath}/tapa_armonia_ilustrada.jpg`)}
              >
                <Image
                  src={`/book/${langFromPath}/tapa_armonia_ilustrada.jpg`}
                  alt={t("illustrated_harmony_title")}
                  width={200} height={coverH(200)} quality={60}
                  style={{ width: 160, height: "auto", boxShadow: "0 4px 15px rgba(0,0,0,0.15)", border: "3px solid #d0ac22", filter: armoniaOwned ? "grayscale(1)" : "none", opacity: armoniaOwned ? 0.55 : 1 }}
                />
              </div>
              <ul style={{ color: "#555", fontSize: 15, textAlign: "center", marginBottom: 16, paddingLeft: 0, lineHeight: 2, listStyle: "none", margin: "0 auto 16px" }}>
                {(t("l2_armonia_details", { returnObjects: true }) || []).map((item, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: `✓ ${item}` }} />
                ))}
              </ul>
              <div style={{ marginTop: "auto", textAlign: "center" }}>
                {armoniaOwned ? (
                  <>
                    <div style={{ border: "2px solid #2e7d32", color: "#2e7d32", borderRadius: 999, padding: "12px 16px", fontWeight: 700, fontSize: 15 }}>
                      ✓ {t("l2_owned_badge")}
                    </div>
                    <a
                      href={localePath("/libro/ai")}
                      style={{ display: "inline-block", marginTop: 12, fontSize: 13, color: "#bbb", textDecoration: "underline", textUnderlineOffset: 3 }}
                    >
                      {t("l2_see_more")}
                    </a>
                  </>
                ) : (
                  <>
                    {armoniaPrice.price != null && (
                      <div style={{ marginBottom: 12 }}>
                        {armoniaPrice.discountPct > 0 && (
                          <div>
                            <span style={{ background: "#111", color: "#fff", fontSize: 14, padding: "3px 10px", borderRadius: 50, fontWeight: 700 }}>
                              {armoniaPrice.discountPct}% OFF
                            </span>
                          </div>
                        )}
                        {armoniaPrice.hasDiscount && (
                          <span style={{ fontSize: 18, color: "#999", textDecoration: "line-through", whiteSpace: "nowrap", marginTop: 16, display: "inline-block" }}>
                            {currency} {fmtPrice(armoniaPrice.original)}
                          </span>
                        )}
                        <span style={{ fontSize: 30, fontWeight: 700, color: "#111", whiteSpace: "nowrap", display: "block", lineHeight: "35px" }}>
                          {currency} {fmtPrice(armoniaPrice.price)}
                        </span>
                      </div>
                    )}
                    <div style={{ background: "linear-gradient(120deg, #d0ac22, #d0ac22)", padding: "3px", borderRadius: "999px" }}>
                      <button
                        type="button"
                        onClick={onBuyBookClick(PRODUCT_IDS.ARMONIA)}
                        className="btn btn-mod btn-circle btn-large"
                        style={{ background: "#fff", border: "none", borderRadius: "999px", color: "#111", width: "100%" }}
                        data-btn-animate="y"
                        disabled={isRedirecting}
                      >
                        {t("buy_button")}
                      </button>
                    </div>
                    <div style={{ fontSize: 12, color: "#999", marginTop: 10 }}>{t("l2_digital_instant")}</div>
                    <a
                      href={localePath("/libro/ai")}
                      style={{ display: "inline-block", marginTop: 12, fontSize: 13, color: "#bbb", textDecoration: "underline", textUnderlineOffset: 3 }}
                    >
                      {t("l2_see_more")}
                    </a>
                  </>
                )}
              </div>
            </div>

            {/* Pack Panorama — destacado. En mobile va primero (order:-1): con 51% de
                órdenes de 1 libro, el layout no debe empujar al ítem más barato (4.1). */}
            <div ref={packPanoramaRef} style={{
              background: "#fff",
              borderRadius: 16,
              padding: "30px 24px",
              flex: 1,
              maxWidth: 340,
              width: "100%",
              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
              border: "2px solid #111",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              order: isWideScreen ? undefined : -1,
            }}>
              <div style={{
                position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)",
                background: "#111", color: "#fff", fontSize: 12, fontWeight: 600,
                padding: "4px 16px", borderRadius: 50, whiteSpace: "nowrap",
              }}>
                {t("l2_most_popular")}
              </div>
              <h3 style={{ fontSize: 28, fontWeight: 700, color: "#111", textAlign: "center", marginBottom: 16 }}>
                {t("pack")} <span style={{ fontStyle: "italic" }}>{t("pack_panorama")}</span>
              </h3>
              <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                {[
                  { src: "tapa_armonia_ilustrada.jpg", border: "#d0ac22" },
                  { src: "tapa_modos_ilustrados.jpg", border: "#e588b5" },
                ].map((b, i) => (
                  <Image
                    key={i}
                    src={`/book/${langFromPath}/${b.src}`}
                    alt="" width={120} height={coverH(120)} quality={60}
                    style={{ width: 90, height: "auto", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", border: `2px solid ${b.border}`, cursor: "pointer" }}
                    onClick={() => setLightboxImage(`/book/${langFromPath}/${b.src}`)}
                  />
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                <Image
                  src={`/book/${langFromPath}/tapa_tensiones_ilustradas.jpg`}
                  alt="" width={120} height={coverH(120)} quality={60}
                  style={{ width: 90, height: "auto", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", border: "2px solid #81d0e1", cursor: "pointer" }}
                  onClick={() => setLightboxImage(`/book/${langFromPath}/tapa_tensiones_ilustradas.jpg`)}
                />
              </div>
              <ul style={{ color: "#555", fontSize: 15, textAlign: "center", marginBottom: 16, paddingLeft: 0, lineHeight: 2, listStyle: "none", margin: "0 auto 16px" }}>
                {(t("l2_panorama_details", { returnObjects: true }) || []).map((item, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: `✓ ${item}` }} />
                ))}
              </ul>
              <div style={{ marginTop: "auto", textAlign: "center" }}>
                {completing && (
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#2e7d32", marginBottom: 8 }}>
                    {t("l2_owned_includes_armonia")}
                  </div>
                )}
                {panoramaPP.allPricesAvailable && (
                  <div style={{ marginBottom: 12 }}>
                    {panoramaPP.discountPct > 0 && (
                      <div>
                        <span style={{ background: "#111", color: "#fff", fontSize: 14, padding: "3px 10px", borderRadius: 50, fontWeight: 700 }}>
                          {panoramaPP.discountPct}% OFF
                        </span>
                      </div>
                    )}
                    <span style={{ fontSize: 18, color: "#999", textDecoration: "line-through", whiteSpace: "nowrap", marginTop: 16, display: "inline-block" }}>
                      {currency} {fmtPrice(panoramaPP.original)}
                    </span>
                    <span style={{ fontSize: 30, fontWeight: 700, color: "#111", whiteSpace: "nowrap", display: "block", lineHeight: "35px" }}>
                      {currency} {fmtPrice(panoramaPP.final)}
                    </span>
                  </div>
                )}
                <div style={{
                  background: "linear-gradient(120deg, #d0ac22 0%, #e588b5 50%, #81d0e1 100%)",
                  padding: "3px", borderRadius: "999px",
                }}>
                  <button
                    type="button"
                    onClick={onBuyPanorama}
                    className="btn btn-mod btn-circle btn-large"
                    style={{ background: "#fff", border: "none", borderRadius: "999px", color: "#111", width: "100%" }}
                    data-btn-animate="y"
                    disabled={isRedirecting}
                  >
                    {t("buy_button")}
                  </button>
                </div>
                <div style={{ fontSize: 12, color: "#999", marginTop: 10 }}>{t("l2_digital_instant")}</div>
                <a
                  href={localePath("/pack/panorama")}
                  style={{ display: "inline-block", marginTop: 12, fontSize: 13, color: "#bbb", textDecoration: "underline", textUnderlineOffset: 3 }}
                >
                  {t("l2_see_more")}
                </a>
              </div>
            </div>

            {/* Pack Futuro */}
            <div style={{
              background: "#fff",
              borderRadius: 16,
              padding: "30px 24px",
              flex: 1,
              maxWidth: 340,
              width: "100%",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              display: "flex",
              flexDirection: "column",
            }}>
              <h3 style={{ fontSize: 28, fontWeight: 700, color: "#111", textAlign: "center", marginBottom: 16 }}>
                {t("pack")} <span style={{ fontStyle: "italic" }}>{t("pack_futuro")}</span>
              </h3>
              {/* Las tapas salen de PACK_CONFIG.futuro + BOOK_META, no de una lista escrita
                  a mano. Estaban hardcodeadas en dos filas de 3 y 2, y cuando Escalas entro
                  al pack la tarjeta paso a decir "6 libros" mostrando 5 tapas. Derivarlas es
                  lo unico que impide que vuelva a pasar en el proximo libro.
                  Ojo con la extension: no todas son .jpg (la de Escalas es .png), por eso se
                  usa meta.cover entero y no se arma el nombre por convencion. */}
              {/* Un solo contenedor que envuelve, en vez de dos filas fijas de 3. Tres tapas
                  piden 241px y la tarjeta deja 236 en la grilla de escritorio, asi que un
                  corte 3/3 se parte en 2+1 y 2+1, desparejo. Dejandolo fluir da 2+2+2 en
                  escritorio y 3+3 en mobile, que es donde se mira. */}
              <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                {PACK_CONFIG.futuro.map((id) => {
                  const meta = BOOK_META[id];
                  const src = `/book/${langFromPath}/${meta.cover}`;
                  return (
                    <Image
                      key={id}
                      src={src}
                      alt="" width={120} height={coverH(120)} quality={60}
                      style={{ width: 75, height: "auto", boxShadow: "0 2px 6px rgba(0,0,0,0.1)", border: `2px solid ${meta.color}`, cursor: "pointer" }}
                      onClick={() => setLightboxImage(src)}
                    />
                  );
                })}
              </div>
              <ul style={{ color: "#555", fontSize: 15, textAlign: "center", marginBottom: 16, paddingLeft: 0, lineHeight: 2, listStyle: "none", margin: "0 auto 16px" }}>
                {(t("l2_full_details", { returnObjects: true }) || []).map((item, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: `✓ ${item}` }} />
                ))}
              </ul>
              <div style={{ marginTop: "auto", textAlign: "center" }}>
                {completing && (
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#2e7d32", marginBottom: 8 }}>
                    {t("l2_owned_includes_armonia")}
                  </div>
                )}
                {futuroPP.allPricesAvailable && (
                  <div style={{ marginBottom: 12 }}>
                    {futuroPP.discountPct > 0 && (
                      <div>
                        <span style={{ background: "#111", color: "#fff", fontSize: 14, padding: "3px 10px", borderRadius: 50, fontWeight: 700 }}>
                          {futuroPP.discountPct}% OFF
                        </span>
                      </div>
                    )}
                    <span style={{ fontSize: 18, color: "#999", textDecoration: "line-through", whiteSpace: "nowrap", marginTop: 16, display: "inline-block" }}>
                      {currency} {fmtPrice(futuroPP.original)}
                    </span>
                    <span style={{ fontSize: 30, fontWeight: 700, color: "#111", whiteSpace: "nowrap", display: "block", lineHeight: "35px" }}>
                      {currency} {fmtPrice(futuroPP.final)}
                    </span>
                  </div>
                )}
                <div style={{
                  background: "linear-gradient(120deg, #d0ac22 0%, #e588b5 25%, #81d0e1 50%, #95ca44 75%, #e29f53 100%)",
                  padding: "3px", borderRadius: "999px",
                }}>
                  <button
                    type="button"
                    onClick={onBuyFuturo}
                    className="btn btn-mod btn-circle btn-large"
                    style={{ background: "#fff", border: "none", borderRadius: "999px", color: "#111", width: "100%" }}
                    data-btn-animate="y"
                    disabled={isRedirecting}
                  >
                    {t("buy_button")}
                  </button>
                </div>
                <div style={{ fontSize: 12, color: "#999", marginTop: 10 }}>{t("l2_digital_instant")}</div>
                <a
                  href={localePath("/pack/futuro")}
                  style={{ display: "inline-block", marginTop: 12, fontSize: 13, color: "#bbb", textDecoration: "underline", textUnderlineOffset: 3 }}
                >
                  {t("l2_see_more")}
                </a>
              </div>
            </div>
          </div>
          )}

          {/* 4.2 (quotes junto a las cards) se probó y se revirtió: quedaban raros
              duplicando la sección de testimonios que está justo abajo. */}

          {/* Links secundarios */}
          <div style={{ textAlign: "center", marginTop: 28, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            {ownedBaseIds && ownershipMode !== "default" && (
              <button
                type="button"
                onClick={resetOwned}
                style={{ color: "#aaa", fontSize: 14, textDecoration: "underline", textUnderlineOffset: 3, background: "none", border: "none", cursor: "pointer" }}
              >
                {t("l2_owned_change")}
              </button>
            )}
            <a
              href={localePath("/libros-catalogo")}
              style={{ color: "#aaa", fontSize: 14, textDecoration: "underline", textUnderlineOffset: 3 }}
            >
              {t("l2_see_full_catalog")}
            </a>
          </div>
        </div>
      </section>

      {/* ========== SECCIÓN 5 — TESTIMONIOS ========== */}
      {/* Era una copia inline de la misma grilla de imágenes que ya existía en
          _collection/Testimonials. Ahora usa el compartido, que publica el texto
          del mensaje en HTML y deja la captura a un click. */}
      <Testimonials
        locale={langFromPath}
        title={t("l2_testimonials_title")}
        onZoom={setLightboxImage}
        isWide={isWideScreen}
        background="#fff"
      />

      {/* ========== SECCIÓN 6 — FAQ ========== */}
      <section style={{ background: "#f8f8f8", padding: "60px 20px" }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: "#111", textAlign: "center", marginBottom: 40 }}>
            {t("l2_faq_title")}
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {faqArray.map((item, i) => (
              <div key={i} style={{ borderBottom: "1px solid #e0e0e0" }}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  aria-controls={`l2-faq-${i}`}
                  style={{
                    width: "100%",
                    background: "none",
                    border: "none",
                    padding: "18px 0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <span style={{ fontSize: 16, fontWeight: 600, color: "#111", paddingRight: 16 }}>
                    {item.question}
                  </span>
                  <span style={{
                    fontSize: 20, color: "#999", flexShrink: 0,
                    transform: openFaq === i ? "rotate(45deg)" : "none",
                    transition: "transform 0.2s",
                  }}>
                    +
                  </span>
                </button>
                {/* Siempre en el DOM, oculto por CSS. Si se desmonta, Google no
                    lo ve nunca (no interactúa con la página) y el texto igual
                    viajaba en el payload: se pagaba el peso sin cobrar el SEO. */}
                <div
                  id={`l2-faq-${i}`}
                  style={{ paddingBottom: 18, display: openFaq === i ? "block" : "none" }}
                >
                  <p
                    className="text-gray"
                    style={{ fontSize: 15, lineHeight: 1.6, margin: 0 }}
                    dangerouslySetInnerHTML={{ __html: item.answer }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <a
              href={localePath("/libros-fisicos")}
              style={{ color: "#aaa", fontSize: 14, textDecoration: "underline", textUnderlineOffset: 3 }}
            >
              {t("l2_buy_physical")}
            </a>
          </div>
        </div>
      </section>

      {/* Slot (ej. NewsletterSignup): va ANTES del CTA final, así "Empezá a componer"
          cierra la página debajo del "Stay in the loop". */}
      {children}

      {/* ========== SECCIÓN 7 — CTA FINAL ========== */}
      <section style={{ background: "#fff", padding: "60px 20px", textAlign: "center" }}>
        <p className="text-gray" style={{ fontSize: 18, marginBottom: 24 }}>
          {t("l2_cta_text")}
        </p>
        <div
          style={{
            background: "linear-gradient(120deg, #c45646 0%, #c45646 30%, #475b7e 35%, #475b7e 65%, #e29f53 70%, #e29f53 100%)",
            padding: "2px",
            borderRadius: "999px",
            display: "inline-block",
          }}
        >
          <button
            type="button"
            onClick={scrollToFirstPack}
            className="btn btn-mod btn-circle btn-large"
            style={{ background: "#fff", border: "none", borderRadius: "999px", color: "#111" }}
            data-btn-animate="y"
          >
            {t("l2_cta_button")}
          </button>
        </div>
      </section>

      {/* CTA sticky de compra (2.4, solo mobile): el scroll promedio de /libros es 32% —
          la mayoría nunca llega a los precios. La barra aparece cuando el COMPRAR del
          hero sale del viewport y se esconde mientras la sección de productos está a
          la vista. Siempre montada (transform) para que la transición sea suave. */}
      {!isWideScreen && ownershipMode !== "all-owned" && (() => {
        const showStickyCta = !heroCtaInView && !productsInView && !isRedirecting;
        return (
          <div
            aria-hidden={!showStickyCta}
            data-bcm-sticky-bar
            style={{
              position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 900,
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
              padding: "10px 16px calc(10px + env(safe-area-inset-bottom))",
              background: "rgba(17,17,17,0.96)", backdropFilter: "blur(6px)",
              boxShadow: "0 -4px 18px rgba(0,0,0,0.28)",
              transform: showStickyCta ? "translateY(0)" : "translateY(110%)",
              transition: "transform .28s ease",
              pointerEvents: showStickyCta ? "auto" : "none",
            }}
          >
            <div style={{ minWidth: 0, color: "#fff" }}>
              <div style={{ fontSize: 15, fontWeight: 700, whiteSpace: "nowrap" }}>
                {armoniaPrice.price != null
                  ? t("l2_hero_from_price", { price: `${currency} ${fmtPrice(armoniaPrice.price)}` })
                  : t("l2_digital_instant")}
              </div>
              <div style={{ fontSize: 11, color: "#aaa", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {t("l2_hero_proof")}
              </div>
            </div>
            <div style={{ background: "linear-gradient(120deg, #c45646 0%, #c45646 30%, #475b7e 35%, #475b7e 65%, #e29f53 70%, #e29f53 100%)", padding: 2, borderRadius: 999, flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => { try { sendGA("sticky_cta_click", { page_type: "libros" }); } catch {} scrollToFirstPack(); }}
                style={{
                  background: "#fff", border: "none", borderRadius: 999, color: "#111",
                  padding: "11px 26px", fontSize: 13, fontWeight: 700,
                  letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer",
                }}
              >
                {t("buy_button")}
              </button>
            </div>
          </div>
        );
      })()}

      {/* Modal "¿Ya compraste alguno de los libros?" */}
      {showOwnedModal && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={() => setShowOwnedModal(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: 16, padding: "28px 24px", maxWidth: 380, width: "100%", position: "relative" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowOwnedModal(false)}
              aria-label={t("close")}
              style={{ position: "absolute", top: 12, right: 14, background: "transparent", border: "none", color: "#999", fontSize: 22, cursor: "pointer", lineHeight: 1 }}
            >
              ✕
            </button>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: "#111", margin: "0 0 8px" }}>
              {t("l2_owned_modal_title")}
            </h3>
            <p style={{ color: "#666", fontSize: 14, margin: "0 0 16px", lineHeight: 1.5 }}>
              {t("l2_owned_modal_desc")}
            </p>
            <form onSubmit={submitOwnedModal}>
              <input
                type="email"
                value={modalEmail}
                onChange={(e) => { setModalEmail(e.target.value); if (ownedError) setOwnedError(""); }}
                placeholder={t("l2_owned_modal_placeholder")}
                autoComplete="email"
                autoFocus
                style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: `1px solid ${ownedError ? "#e05a5a" : "#ddd"}`, fontSize: 15, outline: "none", marginBottom: ownedError ? 6 : 16, boxSizing: "border-box" }}
              />
              {ownedError && <div style={{ color: "#e05a5a", fontSize: 13, marginBottom: 12 }}>{ownedError}</div>}
              <button
                type="submit"
                disabled={ownedLoading}
                className="btn btn-mod btn-circle btn-large"
                style={{ width: "100%", borderRadius: 999, opacity: ownedLoading ? 0.7 : 1 }}
              >
                {ownedLoading ? t("l2_owned_modal_loading") : t("l2_owned_modal_submit")}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {showAppModal && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={() => setShowAppModal(null)}
        >
          <div
            style={{ background: "#222", borderRadius: 16, padding: "30px 24px", maxWidth: 340, width: "100%", textAlign: "center", position: "relative" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowAppModal(null)}
              style={{ position: "absolute", top: 15, right: 15, background: "transparent", border: "none", color: "#888", fontSize: 24, cursor: "pointer", padding: 5 }}
              aria-label={t("close")}
            >
              ✕
            </button>
            <Image
              src={`/assets/images/apps/${showAppModal.img}`}
              alt={t(`app_${showAppModal.key}`)}
              width={120}
              height={120}
              quality={60}
              style={{ width: 120, height: 120, borderRadius: 24, marginBottom: 20 }}
            />
            <h3 style={{ color: "#fff", fontSize: 22, fontWeight: 700, margin: "0 0 15px" }}>
              {t(`app_${showAppModal.key}`)}
            </h3>
            <p style={{ color: "#bbb", fontSize: 14, lineHeight: 1.6, margin: "0 0 20px" }}>
              {t(`app_${showAppModal.key}_description`)}
            </p>
            {/* 4.5 se probó con links a /libro/{slug} y se revirtió: sacaba a la gente
                de la landing hacia otras páginas. Queda como texto informativo. */}
            {(showAppModal.includedWithKey
              ? [{ key: showAppModal.includedWithKey, color: showAppModal.includedColor }]
              : showAppModal.includedWith || []
            ).map((entry, idx) => (
              <p key={idx} style={{ color: entry.color || "#b5e3f3", fontSize: 13, fontWeight: 600, margin: idx === 0 ? 0 : "4px 0 0" }}>
                {t("included_with_book", { bookName: t(entry.key) })}
              </p>
            ))}
            <button
              onClick={() => setShowAppModal(null)}
              style={{ marginTop: 25, padding: "12px 40px", background: "#333", border: "none", borderRadius: 8, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
            >
              {t("close")}
            </button>
          </div>
        </div>
      )}

      {lightboxImage && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0, 0, 0, 0.95)",
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
          }}
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            style={{
              position: "absolute", top: 20, right: 20,
              background: "transparent", border: "none", color: "#fff",
              fontSize: 32, cursor: "pointer", padding: 10,
            }}
            aria-label={t("close")}
          >
            ✕
          </button>
          <Image
            src={lightboxImage}
            alt={t("enlarged_view")}
            width={1200} height={1200} quality={60}
            style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain" }}
          />
        </div>
      )}
    </>
  );
}
